import type { Category, Dictionary, VendorDefinition } from "../types";
import type { BlockedItem } from "../blocking/blocked-items";
import { format } from "../i18n";
import { h, on } from "./dom";
import { PLACEHOLDER_STYLES } from "./styles";

export interface PlaceholderDeps {
  t: Dictionary;
  /** Serialized theme tokens — placeholders live in the light DOM, so they carry their own. */
  tokensCss: string;
  vendorFor(url: string): VendorDefinition | undefined;
  /** Release this one embed without changing the stored decision. */
  onLoadOnce(item: BlockedItem): void;
  /** Grant the whole category, i.e. a real consent decision. */
  onAllowCategory(category: Category): void;
}

const STYLE_ID = "consentkit-placeholder-styles";

/**
 * A blocked YouTube or Maps iframe leaves a hole in the layout, and a hole tells the
 * visitor nothing. This puts a card in its place naming the provider and what loading it
 * costs — the pattern German sites have settled on, and the only lawful way to embed
 * YouTube before consent.
 */
export function mountPlaceholders(items: readonly BlockedItem[], deps: PlaceholderDeps): void {
  const embeds = items.filter((item) => item.kind === "iframe" && !item.released);
  if (embeds.length === 0) return;

  injectLightDomStyles();
  for (const item of embeds) mountOne(item, deps);
}

function mountOne(item: BlockedItem, deps: PlaceholderDeps): void {
  const iframe = item.element as HTMLIFrameElement;
  if (!iframe.parentNode || iframe.dataset["ckPlaceholder"] === "mounted") return;

  const vendor = deps.vendorFor(item.url);
  const provider = vendor?.name ?? hostLabel(item.url);
  const { t } = deps;

  const loadOnce = h("button", { type: "button", class: "ck-btn ck-btn--primary" }, [
    t.placeholder.load,
  ]);
  const allowAlways = h("button", { type: "button", class: "ck-btn ck-btn--ghost" }, [
    format(t.placeholder.loadAlways, { provider }),
  ]);

  const card = h("div", { class: "ck-ph", style: deps.tokensCss, role: "group" }, [
    h("strong", {}, [t.placeholder.title]),
    h("p", { class: "ck-ph-body" }, [format(t.placeholder.body, { provider })]),
    h("div", { class: "ck-ph-actions" }, [loadOnce, allowAlways]),
  ]);

  on(loadOnce, "click", () => {
    card.remove();
    iframe.style.removeProperty("display");
    deps.onLoadOnce(item);
  });
  on(allowAlways, "click", () => deps.onAllowCategory(item.category));

  iframe.dataset["ckPlaceholder"] = "mounted";
  iframe.style.display = "none";
  iframe.parentNode.insertBefore(card, iframe);
}

/** Removes every placeholder — used once the category is granted for real. */
export function clearPlaceholders(): void {
  for (const card of Array.from(document.querySelectorAll(".ck-ph"))) card.remove();
  for (const iframe of Array.from(
    document.querySelectorAll<HTMLIFrameElement>('iframe[data-ck-placeholder="mounted"]'),
  )) {
    iframe.style.removeProperty("display");
    delete iframe.dataset["ckPlaceholder"];
  }
}

function injectLightDomStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = PLACEHOLDER_STYLES;
  (document.head || document.documentElement).appendChild(style);
}

function hostLabel(url: string): string {
  try {
    return new URL(url, window.location.href).hostname.replace(/^www\./, "");
  } catch {
    return "Drittanbieter";
  }
}
